import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getBillingStatus, requestExport } from "../lib/api";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  CreditCard,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileArchive,
  Shield
} from "lucide-react";
import { isUsClinicalHost, USA_PRO_MONTHLY_PRICE_LABEL } from "../lib/usClinicalHost";
import { useAuroraTour } from "../hooks/useAuroraTour";
import { TOUR_CONFIG } from "../lib/auroraTourSteps";

export default function Billing() {
  const isUsHost = isUsClinicalHost();
  const navigate = useNavigate();
  const { isBillingExempt, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const [exporting, setExporting] = useState(false);

  const isLocalDev =
    typeof window !== "undefined" &&
    process.env.REACT_APP_LOCAL_DEV_AUTH === "true" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  const fetchBilling = useCallback(async () => {
    try {
      const res = await getBillingStatus();
      setBilling(res.data);
    } catch (error) {
      if (isLocalDev) {
        setBilling({ plan: "lite", usage: { patients: 0, consultations: 0 }, limits: { patients: 30, consultations: 99999 } });
      } else {
        toast.error(isUsHost ? "Unable to load billing and subscription status." : "Error al cargar datos de facturación");
      }
    } finally {
      setLoading(false);
    }
  }, [isLocalDev, isUsHost]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleRequestExport = async () => {
    setExporting(true);
    try {
      const res = await requestExport();
      toast.success(res.data?.message || (isUsHost ? "Export requested" : "Exportación solicitada"));
    } catch (error) {
      toast.error(error.response?.data?.detail || (isUsHost ? "Unable to request clinical data export." : "Error al solicitar exportación"));
    } finally {
      setExporting(false);
    }
  };

  useAuroraTour(TOUR_CONFIG.descarga.key, TOUR_CONFIG.descarga.steps, {
    enabled: !loading && !isUsHost,
    delay: 850,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const isPremium = billing?.plan === "premium";
  const usage = billing?.usage || {};
  const limits = billing?.limits || {};

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="billing-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/configuracion")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
            {isUsHost ? "Subscription & Billing" : "Facturación"}
          </h1>
          <p className="text-slate-500 text-sm">{isUsHost ? "Plan, trial, and payment" : "Plan y suscripción"}</p>
        </div>
      </div>

      {/* Plan Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-slate-600" />
            {isUsHost ? "Your plan" : "Tu plan"}
          </CardTitle>
          <CardDescription>
            {isPremium
              ? (isUsHost ? "Professional plan — unlimited patients and visits" : "Plan Premium - Uso ilimitado")
              : (isUsHost ? "Lite plan — includes a free trial" : "Plan Lite - Prueba gratuita")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-800">
                {isPremium ? (isUsHost ? "Professional" : "Premium") : "Lite"}
              </p>
              <p className="text-sm text-slate-500">
                {isPremium
                  ? (isUsHost ? "Unlimited patients and visits" : "Pacientes y consultas ilimitados")
                  : isUsHost
                    ? `${usage.patients || 0}/${limits.patients ?? "—"} patients · ${usage.consultations || 0}/${limits.consultations ?? "—"} visits`
                    : `${usage.patients || 0}/${limits.patients ?? "—"} pacientes · ${usage.consultations || 0}/${limits.consultations ?? "—"} consultas`}
              </p>
            </div>
            <Badge variant={isPremium ? "default" : "outline"} className={isPremium ? "bg-emerald-600" : ""}>
              {isPremium ? (isUsHost ? "Active" : "Activo") : (isUsHost ? "Free" : "Gratuito")}
            </Badge>
          </div>
          {!isPremium && !isBillingExempt && (
            <Button
              className="w-full bg-slate-800 hover:bg-slate-700"
              onClick={() =>
                startSubscriptionCheckout({
                  userId: user?.id,
                  email: user?.email,
                  name: user?.nombre,
                })
              }
            >
              {isUsHost
                ? `Upgrade to Pro — ${USA_PRO_MONTHLY_PRICE_LABEL}`
                : "Actualizar a Aurora Geriátrica Profesional - $39/mes"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Export & Cancel */}
      <Card className="border-slate-200" data-aurora-tour="descarga-export">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-slate-600" />
            {isUsHost ? "Export data" : "Exportar datos"}
          </CardTitle>
          <CardDescription>
            {isUsHost
              ? "Download your patients, visits, assessments, and clinical documents as a ZIP file"
              : "Descarga tus pacientes, consultas, evaluaciones y documentos clínicos en un archivo ZIP"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            {isUsHost
              ? "You can cancel anytime and download all data before closing your account."
              : "Puede cancelar en cualquier momento y descargar todos sus datos antes de cerrar su cuenta."}
          </p>
          <div data-aurora-tour="descarga-formatos" className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
            {isUsHost
              ? "Available formats: CSV and Excel. Aurora does not retain your clinical data."
              : "Formatos disponibles: CSV y Excel. Aurora no retiene ningún dato tuyo."}
          </div>
          <Button
            variant="outline"
            onClick={handleRequestExport}
            disabled={exporting || !isPremium}
            className="border-slate-200"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            {exporting ? (isUsHost ? "Preparing..." : "Preparando...") : (isUsHost ? "Request export" : "Solicitar exportación")}
          </Button>
          {!isPremium && (
            <p className="text-xs text-slate-500">
              {isUsHost
                ? "Full export is available on the Professional plan."
                : "La exportación completa está disponible en el plan Premium."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cancel subscription */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-800">
            <Shield className="h-5 w-5 text-slate-600" />
            {isUsHost ? "Cancel subscription" : "Cancelar suscripción"}
          </CardTitle>
          <CardDescription>
            {isUsHost
              ? "If you cancel, you can continue using Aurora Geriatric until the end of your billing period. Download your data before leaving."
              : "Si cancela, podrá seguir usando Aurora Geriátrica hasta el final del período facturado. Antes de irse, descargue sus datos."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
            onClick={() => window.open("https://app.lemonsqueezy.com/my-orders", "_blank")}
          >
            {isUsHost ? "Cancel plan" : "Cancelar plan"}
          </Button>
          <p className="text-xs text-slate-500 mt-3">
            {isUsHost
              ? "Cancellation is available from your Lemon Squeezy customer portal."
              : "La cancelación está disponible desde tu portal de cliente Lemon Squeezy."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
